import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { getCollegeDistribution, getTopPartners, getMonthlyRecovery, getTopTerritories } from '../services/api';
import { COLLEGES, findCollege } from '../constants/colleges';
import { Users, Handshake, ChartLineUp, MapPin } from '@phosphor-icons/react';

const fmtEUR = (n) => (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function ReportingExtras() {
  const [college, setCollege] = useState({ items: [], total: 0 });
  const [partners, setPartners] = useState([]);
  const [recovery, setRecovery] = useState([]);
  const [territories, setTerritories] = useState([]);

  useEffect(() => {
    getCollegeDistribution().then(r => setCollege(r.data)).catch(() => {});
    getTopPartners(5).then(r => setPartners(r.data.items || [])).catch(() => {});
    getMonthlyRecovery(6).then(r => setRecovery(r.data.items || [])).catch(() => {});
    getTopTerritories(5).then(r => setTerritories(r.data.items || [])).catch(() => {});
  }, []);

  // Donut collèges (SVG)
  const total = Math.max(1, college.total);
  let acc = 0;
  const donut = college.items.map(item => {
    const c = findCollege(item.college);
    const frac = item.count / total;
    const start = acc;
    const end = acc + frac;
    acc = end;
    // arc path
    const r = 60, cx = 80, cy = 80;
    const a1 = start * 2 * Math.PI - Math.PI / 2;
    const a2 = end * 2 * Math.PI - Math.PI / 2;
    const large = end - start > 0.5 ? 1 : 0;
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    return { ...item, color: c?.color || '#5E6D66', label: c?.label || item.college, d, frac };
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="reporting-extras">
      {/* Répartition Collèges */}
      <Card className="border-[#E8E6E1]" data-testid="donut-colleges">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users size={18} className="text-[#2D5A4C]" />
            <h3 className="text-sm font-semibold text-[#1A2421]">Répartition par collège</h3>
            <Badge variant="outline" className="text-[10px] ml-auto">{college.total} associés</Badge>
          </div>
          {college.total === 0 ? (
            <p className="text-sm text-[#5E6D66] italic text-center py-6">Aucun contact avec collège renseigné.</p>
          ) : (
            <div className="flex items-center gap-4">
              <svg viewBox="0 0 160 160" className="w-40 h-40 flex-shrink-0">
                {donut.filter(d => d.frac > 0).map((d, i) => (
                  <path key={i} d={d.d} fill={d.color} />
                ))}
                <circle cx="80" cy="80" r="35" fill="#FFFFFF" />
                <text x="80" y="78" textAnchor="middle" className="text-xs fill-[#5E6D66]">Total</text>
                <text x="80" y="92" textAnchor="middle" className="text-lg font-bold fill-[#1A2421]">{college.total}</text>
              </svg>
              <div className="flex-1 space-y-1">
                {college.items.map(item => {
                  const c = findCollege(item.college);
                  const I = c?.Icon;
                  return (
                    <div key={item.college} className="flex items-center gap-2 text-xs">
                      {I && <I size={12} style={{ color: c.color }} />}
                      <span className="font-semibold w-4">{item.college}</span>
                      <span className="flex-1 text-[#5E6D66] truncate">{c?.label}</span>
                      <span className="tabular-nums text-[#1A2421]">{item.count}</span>
                      <span className="tabular-nums text-[#5E6D66]">({item.percentage}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top partenaires */}
      <Card className="border-[#E8E6E1]" data-testid="top-partners">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Handshake size={18} className="text-[#2D5A4C]" />
            <h3 className="text-sm font-semibold text-[#1A2421]">Top partenaires actifs</h3>
          </div>
          {partners.length === 0 ? (
            <p className="text-sm text-[#5E6D66] italic text-center py-6">Aucun partenaire actif.</p>
          ) : (
            <ul className="space-y-2">
              {partners.map((p, i) => (
                <li key={p.id} className="flex items-center gap-3 text-sm">
                  <span className="w-6 h-6 rounded-full bg-[#2D5A4C]/10 flex items-center justify-center text-xs font-bold text-[#2D5A4C]">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1A2421] truncate">{p.nom}</p>
                    <p className="text-xs text-[#5E6D66]">{p.type_partenaire} · {p.niveau_relation}</p>
                  </div>
                  <span className="tabular-nums text-sm font-semibold text-[#1A2421]">{fmtEUR(p.montant_convention)} €</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Recouvrement mensuel */}
      <Card className="border-[#E8E6E1]" data-testid="monthly-recovery">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <ChartLineUp size={18} className="text-[#2D5A4C]" />
            <h3 className="text-sm font-semibold text-[#1A2421]">Taux de recouvrement mensuel</h3>
          </div>
          {recovery.length === 0 ? (
            <p className="text-sm text-[#5E6D66] italic text-center py-6">Aucun paiement sur la période.</p>
          ) : (
            <div className="space-y-2">
              {recovery.map(m => (
                <div key={m.month} className="text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-[#1A2421]">{m.month}</span>
                    <span className="tabular-nums text-[#5E6D66]">{fmtEUR(m.recu)} / {fmtEUR(m.attendu)} €</span>
                    <span className="tabular-nums font-semibold text-[#2D5A4C] w-16 text-right">{m.rate}%</span>
                  </div>
                  <div className="w-full h-2 bg-[#F1EFE9] rounded-full overflow-hidden">
                    <div className="h-full bg-[#2D5A4C]" style={{ width: `${Math.min(100, m.rate)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top territoires opps */}
      <Card className="border-[#E8E6E1]" data-testid="top-territories">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={18} className="text-[#2D5A4C]" />
            <h3 className="text-sm font-semibold text-[#1A2421]">Top territoires (opportunités)</h3>
          </div>
          {territories.length === 0 ? (
            <p className="text-sm text-[#5E6D66] italic text-center py-6">Aucune opportunité.</p>
          ) : (
            <ul className="space-y-2">
              {territories.map((t, i) => (
                <li key={t.territoire} className="flex items-center gap-3 text-sm">
                  <span className="w-6 h-6 rounded-full bg-[#8DB0A1]/20 flex items-center justify-center text-xs font-bold text-[#2D5A4C]">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1A2421] truncate">{t.territoire}</p>
                    <p className="text-xs text-[#5E6D66]">{t.count} opps · {t.converti} converties ({t.taux}%)</p>
                  </div>
                  <span className="tabular-nums text-sm font-semibold text-[#1A2421]">{fmtEUR(t.montant)} €</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
